import React from "react";
import { Link } from "react-router-dom";
import { Phone, Check, ArrowRight, ShieldCheck, Briefcase, Clock, Star, Plane, HeartPulse, Building2, UserCheck } from "lucide-react";
import { SITE } from "../data/site";
import QuoteForm from "../components/QuoteForm";

const STEPS = [
  { n: "01", title: "Schedule Your Chauffeur", desc: "Reserve online or by phone — 24/7." },
  { n: "02", title: "Chauffeur Arrives", desc: "Punctual, uniformed, professional — at your door." },
  { n: "03", title: "Relax While We Drive", desc: "We drive your car. You enjoy the ride." },
];

const SERVICES = [
  { icon: Briefcase, title: "Executive Business Transportation" },
  { icon: Plane, title: "Airport Transfers — IAD · DCA · BWI · FBO" },
  { icon: Star, title: "Nights Out & Events" },
  { icon: HeartPulse, title: "Medical Appointments" },
  { icon: Clock, title: "Long-Distance Driving" },
  { icon: UserCheck, title: "Senior & Family Assistance" },
];

const PRICING = [
  { v: "Executive Sedan", rate: "$65 – $95 / hr", note: "3-hour minimum" },
  { v: "Luxury SUV", rate: "$85 – $125 / hr", note: "3-hour minimum" },
  { v: "Overnight Chauffeur", rate: "Custom Pricing", note: "Multi-day packages" },
  { v: "Event Standby", rate: "Custom Pricing", note: "Per-event quote" },
];

export default function ExecutiveChauffeur() {
  return (
    <div className="pt-20 bg-[#050505] min-h-screen" data-testid="exec-chauffeur-page">
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <img src={SITE.sedanMercedes} alt="Executive Chauffeur For Hire" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/70 to-black/30" />
        <div className="relative max-w-7xl mx-auto px-6 pb-20 w-full grid lg:grid-cols-12 gap-10 items-end pt-32">
          <div className="lg:col-span-8">
            <div className="label-eyebrow mb-4">Executive Chauffeur For Hire</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight leading-[1.02]">
              We Drive Your Vehicle —<br />You Relax
            </h1>
            <div className="mt-6 text-xs tracking-[0.4em] uppercase text-[#d4af6a]">
              Luxury · Privacy · Convenience · Professionalism
            </div>
            <p className="text-white/75 text-lg mt-6 max-w-2xl italic font-display">
              "Your vehicle. Our professional chauffeur."
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a href="#reserve-chauffeur" className="btn-accent" data-testid="exec-reserve">Reserve a Chauffeur <ArrowRight size={14} /></a>
              <a href={`tel:${SITE.phonesTel.virginia}`} className="btn-outline" data-testid="exec-call">
                <Phone size={14} /> Call {SITE.phones.virginia}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-8">
          <div className="lux-card p-8" data-testid="exec-why-1">
            <div className="label-eyebrow mb-2">01</div>
            <h3 className="font-display text-2xl text-white mb-3">Executive-Level Convenience</h3>
            <p className="text-white/65 text-sm leading-relaxed">
              Avoid driving stress, traffic, parking, and long-distance fatigue — while enjoying the comfort of your own vehicle.
            </p>
          </div>
          <div className="lux-card p-8" data-testid="exec-why-2">
            <div className="label-eyebrow mb-2">02</div>
            <h3 className="font-display text-2xl text-white mb-3">Professional Chauffeurs</h3>
            <p className="text-white/65 text-sm leading-relaxed">
              Executive transportation training, defensive driving, client confidentiality, and VIP service standards on every assignment.
            </p>
          </div>
          <div className="lux-card p-8" data-testid="exec-why-3">
            <div className="label-eyebrow mb-2">03</div>
            <h3 className="font-display text-2xl text-white mb-3">Discreet & Confidential</h3>
            <p className="text-white/65 text-sm leading-relaxed">
              Perfect for business executives, attorneys, government officials, medical professionals, elderly clients, families, and special events.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <div className="label-eyebrow mb-3">Our Chauffeur Services</div>
            <h2 className="font-display text-4xl text-white tracking-tight">A professional driver — for every chapter of your life.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s) => (
              <div key={s.title} className="lux-card p-6 flex items-center gap-4" data-testid={`exec-service-${s.title.replace(/\s+/g, "-").toLowerCase().slice(0, 20)}`}>
                <s.icon size={22} className="text-[#d4af6a] flex-shrink-0" />
                <span className="text-white text-sm">{s.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <div className="label-eyebrow mb-3">How It Works</div>
            <h2 className="font-display text-4xl text-white tracking-tight">Three steps to the perfect drive.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {STEPS.map((s) => (
              <div key={s.n} className="lux-card p-8" data-testid={`exec-step-${s.n}`}>
                <div className="font-display text-5xl text-[#9B1B30] leading-none mb-4">{s.n}</div>
                <h4 className="font-display text-2xl text-white mb-2">{s.title}</h4>
                <p className="text-white/60 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY USA LIMO */}
      <section className="py-24 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="label-eyebrow mb-3">Why USA Limo Service</div>
            <h2 className="font-display text-4xl text-white tracking-tight">Trusted Since 1984.</h2>
            <p className="text-white/65 mt-5 leading-relaxed">
              Forty years of executive transportation experience — backed by professionally trained chauffeurs,
              background-checked drivers, and a 24/7 dispatch team that never sleeps.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {["Professionally trained chauffeurs", "Background-checked drivers", "Fully insured", "Available 24/7", "Executive-level service", "Nationwide availability", "Corporate accounts", "Confidential & discreet"].map((p) => (
              <div key={p} className="flex items-center gap-3 text-white/80 text-sm">
                <ShieldCheck size={16} className="text-[#d4af6a] flex-shrink-0" /> {p}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <div className="label-eyebrow mb-3">Flexible Chauffeur Pricing</div>
            <h2 className="font-display text-4xl text-white tracking-tight">Transparent hourly rates. Custom packages.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PRICING.map((p) => (
              <div key={p.v} className="lux-card p-7" data-testid={`exec-price-${p.v.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="label-eyebrow mb-2">{p.v}</div>
                <div className="font-display text-3xl text-white">{p.rate}</div>
                <div className="text-xs text-white/50 mt-2 tracking-wider uppercase">{p.note}</div>
              </div>
            ))}
          </div>
          <p className="text-sm text-white/55 mt-6">
            Long-distance packages, multi-day chauffeur service, event standby, and security chauffeur options are also available. Call for a custom quote.
          </p>
        </div>
      </section>

      {/* CORPORATE */}
      <section className="py-24 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <div className="label-eyebrow mb-3">Corporate Accounts</div>
            <h2 className="font-display text-4xl text-white tracking-tight">For CEOs, law firms, government contractors, embassies & VIPs.</h2>
            <ul className="mt-7 space-y-3">
              {["Priority scheduling", "Monthly billing", "Dedicated chauffeurs", "Confidential service"].map((p) => (
                <li key={p} className="flex items-start gap-3 text-white/80">
                  <Check size={18} className="text-[#d4af6a] mt-0.5" /> {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6 grid sm:grid-cols-2 gap-3">
            {["CEOs", "Law Firms", "Government Contractors", "Embassies", "Medical Offices", "VIP Clients"].map((p) => (
              <div key={p} className="lux-card px-5 py-4 text-white/80 text-sm">{p}</div>
            ))}
          </div>
        </div>
      </section>

      {/* RESERVE */}
      <section id="reserve-chauffeur" className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="max-w-2xl mb-10">
            <div className="label-eyebrow mb-3">Reserve Your Professional Chauffeur Today</div>
            <h2 className="font-display text-4xl text-white tracking-tight">Tell us where, when, and we'll handle the rest.</h2>
            <div className="mt-5 flex flex-wrap gap-4 text-white/75">
              <a href={`tel:${SITE.phonesTel.virginia}`} className="hover:text-white"><Phone size={14} className="inline mr-2" />{SITE.phones.virginia}</a>
              <a href={`tel:${SITE.phonesTel.dispatch}`} className="hover:text-white"><Phone size={14} className="inline mr-2" />{SITE.phones.dispatch}</a>
            </div>
          </div>
          <QuoteForm serviceType="chauffeur" title="Reserve a Chauffeur" eyebrow="Executive Chauffeur" />
        </div>
      </section>
    </div>
  );
}
